import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';


import api from '../utils/axios';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { getRelativeTime } from '../utils/dateUtils';
import { getProjectPosition } from '../utils/projectNavigationStorage';

import CreditViewPanel from './CreditViewPanel';
import ProjectStatsModal from './ProjectStatsModal';
import
{
  CreateProjectModal,
  EditProjectModal,
  CloneProjectModal,
  ManageCollaborationModal,
  CreateProjectUserInput,
} from './ProjectModals';
import { ProjectCard } from './ProjectCard/ProjectCard';
import { ApplicationType } from './ProjectCard/ProjectCard.type';
import { debounce } from '../admin/utils';
import { getNameFromEmail } from '../utils/projectUtils';
import { ProjectMemberWithOwnerResponse } from '../services/projectMembersApi';
import {
  Button,
  Icon,
  Text,
  Input,
  Select,
  MultiSelect,
  Navbar,
  Modal,
} from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import { mapBackendRoleToUiRole, PlatformType } from '../types/project';

export interface Project {
  id: string;
  title: string;
  description: string;
  project_type?: number;
  platform_type?: string;
  tap_url?: string;
  custom_domain?: string;
  app_secret?: string;
  created_at: string;
  updated_at?: string;
  user_id: string;
  client_name?: string;
  readonly_sharable_link_enabled?: boolean;
  is_shared?: boolean;
  prompt_version?: number | null;
  shared_project_info?: ProjectMemberWithOwnerResponse;
  clone_status?: string;
  clone_error?: string;
  clone_retry_count?: number;
}


interface SearchAndFiltersProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  sortBy: string;
  onSortChange: (value: string) => void;
  selectedAppTypes: { web: boolean; tyler: boolean; serviceNow: boolean; powerApps: boolean; salesforce: boolean };
  onAppTypesChange: (types: { web: boolean; tyler: boolean; serviceNow: boolean; powerApps: boolean; salesforce: boolean }) => void;
}

// Skeleton Loading Component for Project Cards
const ProjectCardSkeleton: React.FC<{ index: number }> = ({ index }) => {
  return (
    <div
      className="bg-bg-primary rounded-bg-v2-lg shadow-bg-v2-sm border border-border-secondary p-4 flex items-center gap-4 w-full animate-fade-in-up opacity-0 relative"
      style={{
        animationDelay: `${index * 0.08}s`,
        animationFillMode: 'forwards'
      }}
    >
      {/* Avatar Skeleton */}
      <div className="w-bg-v2-8xl h-bg-v2-8xl rounded-bg-v2-full skeleton-shimmer flex-shrink-0"></div>

      {/* Middle Section - Project Info */}
      <div className="flex-1 min-w-0 flex flex-col justify-center gap-2">
        {/* Project Name and Badge Row */}
        <div className="flex items-center justify-between">
          <div className="h-5 rounded skeleton-shimmer w-2/3"></div>
          <div className="h-5 rounded-full skeleton-shimmer w-16"></div>
        </div>

        {/* Client Name Skeleton */}
        <div className="h-4 rounded skeleton-shimmer w-1/2"></div>

        {/* Date Skeleton */}
        <div className="h-3 rounded skeleton-shimmer w-32"></div>
      </div>

      {/* Three Dot Menu Skeleton - Absolute Positioned Bottom Right */}
      <div className="absolute bottom-4 right-4">
        <div className="w-6 h-6 rounded-full skeleton-shimmer"></div>
      </div>
    </div>
  );
};

const SearchAndFilters: React.FC<SearchAndFiltersProps> = ({
  searchQuery,
  onSearchChange,
  sortBy,
  onSortChange,
  selectedAppTypes,
  onAppTypesChange,
}) => {
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);
  // const [showFilterDropdown, setShowFilterDropdown] = useState(false); // Removed

  // Store latest callback in ref to avoid stale closures
  const onSearchChangeRef = useRef(onSearchChange);

  // Create debounced search handler with lazy initialization
  const debouncedSearchRef = useRef<ReturnType<typeof debounce>>();
  if (!debouncedSearchRef.current) {
    debouncedSearchRef.current = debounce((value: string) => {
      onSearchChangeRef.current(value);
    }, 300);
  }

  // Sync local state with prop and update callback ref
  useEffect(() => {
    onSearchChangeRef.current = onSearchChange;
    setLocalSearchQuery(searchQuery);
  }, [searchQuery, onSearchChange]);

  // Update local state immediately and debounce the actual search
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalSearchQuery(value);
    debouncedSearchRef.current?.(value);
  }, []);

  const sortOptions = [
    { label: 'Last Updated', value: 'updated_at' },
    { label: 'Last Created', value: 'created_at' },
    { label: 'Alphabetic', value: 'alphabetical' },
  ];

  return (
    <div className="flex items-center gap-4 flex-wrap">
      {/* Search Bar */}
      <div className="w-80">
        <Input
          id="search-project"
          name="search-project"
          placeholder="Search Project"
          value={localSearchQuery}
          onChange={handleSearchChange}
          startIcon={<Icon name={IconName.Search} size="sm" className="text-text-tertiary" />}
          className="w-full"
        />
      </div>

      {/* Sort Dropdown */}
      <div className="w-48">
        <Select
          options={sortOptions}
          value={sortBy}
          onChange={(val) => onSortChange(val as string)}
          placeholder="Sort By"
          className="font-bg-v2-regular"
        // size="sm"
        />
      </div>
      {/* Application Type Filter - On the right */}
      <div className="flex items-center gap-bg-v2-md ml-auto">
        <Text
          variant="text-md"
          weight="medium"
          color="disabled"
          className="overflow-hidden text-ellipsis whitespace-nowrap"
        >
          Application Type:
        </Text>
        <div className="w-64">
          <MultiSelect
            id="app-type-filter"
            aria-label="Filter by application type"
            options={[
              { label: 'Web', value: 'web' },
              { label: 'Tyler App Platform', value: 'tyler', initial: 'TAP' },
              { label: 'ServiceNow', value: 'serviceNow', initial: 'SN' },
              { label: 'Power Apps', value: 'powerApps', initial: 'PA' },
              { label: 'Salesforce', value: 'salesforce', initial: 'SF' },
            ]}
            value={Object.entries(selectedAppTypes)
              .filter(([, checked]) => checked)
              .map(([key]) => key)}
            onChange={(next) => {
              const nextSet = new Set(next);
              onAppTypesChange({
                web: nextSet.has('web'),
                tyler: nextSet.has('tyler'),
                serviceNow: nextSet.has('serviceNow'),
                powerApps: nextSet.has('powerApps'),
                salesforce: nextSet.has('salesforce'),
              });
            }}
            placeholder="Select platforms"
            allSelectedLabel="All platforms"
            selectAllLabel="All platforms"
          />
        </div>
      </div>
    </div>
  );
};

type ProjectTab = 'myProjects' | 'sharedWithMe' | 'invitations';

interface SharedProject {
  project_id: string;
  project_title: string;
  project_type: number;
  project_description?: string;
  role: string;
  invited_at?: number;
  accepted_at?: number;
  project_owner_email?: string;
  project_member_id?: string; // ID of the project_member record for API calls
  project_owner_name?: string;
  platform_type?: string;
}

interface ProjectTabsProps {
  activeTab: ProjectTab;
  onTabChange: (tab: ProjectTab) => void;
  myProjectsCount: number;
  sharedProjectsCount: number;
  invitationsCount: number;
}

// Tab Count Badge Component
const TabCountBadge: React.FC<{ count: number }> = ({ count }) => {
  return (
    <div className="bg-bg-primary border border-border-disabled rounded-bg-v2-full shadow-bg-v2-xs flex flex-col items-center justify-center p-bg-v2-xxs shrink-0 w-[22px]">
      <Text variant="text-xs" weight="medium" color="secondary" className="text-text-secondary-hover">
        {count}
      </Text>
    </div>
  );
};

const ProjectTabs: React.FC<ProjectTabsProps> = ({
  activeTab,
  onTabChange,
  myProjectsCount,
  sharedProjectsCount,
  invitationsCount,
}) => {
  const tabs = [
    {
      id: 'myProjects' as ProjectTab,
      label: 'My Projects',
      count: myProjectsCount,
      zIndex: 9,
      hasRightBorder: true,
      paddingLeft: 'px-bg-v2-xl',
    },
    {
      id: 'sharedWithMe' as ProjectTab,
      label: 'Shared with Me',
      count: sharedProjectsCount,
      zIndex: 8,
      hasRightBorder: true,
      paddingLeft: 'px-bg-v2-xl',
    },
    {
      id: 'invitations' as ProjectTab,
      label: 'Invitations',
      count: invitationsCount,
      zIndex: 1,
      hasRightBorder: false,
      paddingLeft: 'px-bg-v2-xl',
    },
  ];

  const getZIndexClass = (zIndex: number) => {
    const zIndexMap: { [key: number]: string } = {
      1: 'z-[1]',
      8: 'z-[8]',
      9: 'z-[9]',
    };
    return zIndexMap[zIndex] || 'z-0';
  };

  return (
    <div className="border border-border-primary rounded-bg-v2-md shadow-bg-v2-xs inline-flex items-start overflow-hidden relative isolate">
      {tabs.map((tab) => {
        const isActive = activeTab === tab.id;
        return (
          <Button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            variant="ghost"
            size="small"
            className={`
              !flex !gap-bg-v2-sm !items-center !justify-center !min-h-[40px] 
              ${tab.paddingLeft} pr-bg-v2-xl py-bg-v2-md 
              !relative !shrink-0 
              ${tab.hasRightBorder ? 'border-r border-border-primary' : ''} 
              ${getZIndexClass(tab.zIndex)}
              !rounded-none
              ${isActive
                ? '!bg-bg-brand-primary-alt border-b-0 border-l-0 border-t-0'
                : '!bg-bg-primary border-b-0 border-l-0 border-t-0'
              }
              !shadow-none
              !h-auto
            `}
            label={
              <>
                <Text
                  variant="text-sm"
                  weight="semibold"
                  color="secondary"
                  className={isActive ? 'text-text-secondary-hover' : ''}
                >
                  {tab.label}
                </Text>
                <TabCountBadge count={tab.count} />
              </>
            }
          />
        );
      })}
      <div className="absolute inset-0 pointer-events-none shadow-[inset_0px_0px_0px_1px_rgba(10,13,18,0.18),inset_0px_-2px_0px_0px_rgba(10,13,18,0.05)]" />
    </div>
  );
};

const Dashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ProjectTab>('myProjects');
  const [projects, setProjects] = useState<Project[]>([]);
  const [sharedProjects, setSharedProjects] = useState<Project[]>([]);
  const [invitations, setInvitations] = useState<SharedProject[]>([]);
  const [filteredProjects, setFilteredProjects] = useState<Project[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('updated_at');
  const [selectedAppTypes, setSelectedAppTypes] = useState<{ web: boolean, tyler: boolean, serviceNow: boolean, powerApps: boolean, salesforce: boolean }>({ web: true, tyler: true, serviceNow: true, powerApps: true, salesforce: true });

  // [info] modal section
  const [showCreditViewPanel, setShowCreditViewPanel] = useState(false);
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);

  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingInvitations, setLoadingInvitations] = useState(false);

  const [acceptingInvitations, setAcceptingInvitations] = useState<{ [key: string]: boolean }>({});
  const [decliningInvitations, setDecliningInvitations] = useState<{ [key: string]: boolean }>({});
  const [leavingProjects, setLeavingProjects] = useState<{ [key: string]: boolean }>({});

  /* ! [info] [todo] project related state should be one object or something */
  const [newProjectUserInput, setNewProjectUserInput] = useState<CreateProjectUserInput>({
    title: '',
    clientName: '',
    projectType: 1,
  });

  const [creatingProject, setCreatingProject] = useState(false);
  const [createProjectError, setCreateProjectError] = useState('');
  const [editModalOpen, setEditModalOpen] = useState(false);

  // [info] edit project section
  const [editProjectId, setEditProjectId] = useState<string | null>(null);
  const [editProjectName, setEditProjectName] = useState('');
  // const [editProjectDesc, setEditProjectDesc] = useState(''); // Removed description state
  const [editProjectClientName, setEditProjectClientName] = useState('');
  const [editProjectCustomDomain, setEditProjectCustomDomain] = useState('');

  // [info] edit project modal section
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');

  // [info] delete project section
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // [info] delete clone confirm modal
  const [deleteCloneConfirm, setDeleteCloneConfirm] = useState<{ projectId: string; cloneStatus?: string } | null>(null);
  const [deleteCloneLoading, setDeleteCloneLoading] = useState(false);
  const [deleteCloneError, setDeleteCloneError] = useState('');

  // [info] clone project section
  const [isCloning, setIsCloning] = useState(false);
  const [cloningProjectIds, setCloningProjectIds] = useState<Set<string>>(new Set());
  const [cloneUserInput, setCloneUserInput] = useState<{
    title?: string;
    clientName?: string;
    projectType?: number;
    platformType?: PlatformType;
  }>();
  const [selectedCloneProject, setSelectedCloneProject] = useState<any | undefined>(undefined);

  // [info] manage collaborations section
  const [isManageCollaborationsModalOpen, setIsManageCollaborationsModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | undefined>(undefined);

  const [showStatsModal, setShowStatsModal] = useState(false);
  const [statsProjectId, setStatsProjectId] = useState<string>('');
  const [statsProjectTitle, setStatsProjectTitle] = useState<string>('');


  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();

  // Note: Removed automatic admin redirect - users can now switch roles manually

  // Check URL hash on mount and when location changes to set active tab
  useEffect(() => {
    const hash = location.hash.toLowerCase();
    if (hash === '#invitation' || hash === '#invitations') {
      setActiveTab('invitations');
    }
  }, [location.hash]);

  useEffect(() => {
    fetchProjects();
    fetchInvitations();
    // Debug: log user and auth state
    console.log('Dashboard user:', user);
    // If you want to log isAuthenticated, you can get it from useAuth
  }, []);


  const fetchInvitations = async () => {
    setLoadingInvitations(true);
    try {
      const res = await api.get('/projects/members/shared', {
        params: { invite_status: 'pending' }
      });
      setInvitations(res.data.projects || []);
    } catch (err) {
      console.error('Failed to fetch invitations:', err);
      setInvitations([]);
    } finally {
      setLoadingInvitations(false);
    }
  };

  // Helper function to get shared project metadata
  const getSharedProjectInfo = (projectId: string): { sharedBy?: string; role?: string; projectMemberId?: string } => {
    if (activeTab === 'sharedWithMe') {
      const sharedProject = sharedProjects.find(sp => sp.id === projectId);
      if (sharedProject && sharedProject.shared_project_info) {
        const memberInfo = sharedProject.shared_project_info;
        // Use project_owner_name from backend, fallback to email formatting
        const ownerEmail = memberInfo.invited_by || '';
        const formattedName = memberInfo.project_owner_name || getNameFromEmail(ownerEmail) || ownerEmail;

        return {
          sharedBy: formattedName,
          role: mapBackendRoleToUiRole(memberInfo.role || ''),
          projectMemberId: memberInfo._id
        };
      }
    } else if (activeTab === 'invitations') {
      const invitation = invitations.find(inv => inv.project_id === projectId);
      if (invitation) {
        const ownerEmail = invitation.project_owner_email || '';
        const formattedName = invitation.project_owner_name || getNameFromEmail(ownerEmail) || ownerEmail;

        return {
          sharedBy: formattedName,
          role: mapBackendRoleToUiRole(invitation.role || ''),
          projectMemberId: invitation.project_member_id
        };
      }
    }
    return {};
  };

  // Handle accepting invitation
  const handleAcceptInvitation = async (projectId: string, projectMemberId?: string) => {
    if (!projectMemberId) {
      console.error('Project member ID is required to accept invitation');
      return;
    }

    setAcceptingInvitations(prev => ({ ...prev, [projectId]: true }));
    try {
      await api.patch(`/projects/${projectId}/members/${projectMemberId}/invite`, {
        status: 'accepted'
      });

      // Refresh invitations and shared projects
      await Promise.all([
        fetchInvitations(),
        fetchProjects(),
      ]);
    } catch (err: any) {
      console.error('Failed to accept invitation:', err);
      alert(err.response?.data?.detail || 'Failed to accept invitation');
    } finally {
      setAcceptingInvitations(prev => ({ ...prev, [projectId]: false }));
    }
  };

  // Handle declining invitation
  const handleDeclineInvitation = async (projectId: string, projectMemberId?: string) => {
    if (!projectMemberId) {
      console.error('Project member ID is required to decline invitation');
      return;
    }

    setDecliningInvitations(prev => ({ ...prev, [projectId]: true }));
    try {
      await api.patch(`/projects/${projectId}/members/${projectMemberId}/invite`, {
        status: 'rejected'
      });

      // Refresh invitations
      await fetchInvitations();
    } catch (err: any) {
      console.error('Failed to decline invitation:', err);
      alert(err.response?.data?.detail || 'Failed to decline invitation');
    } finally {
      setDecliningInvitations(prev => ({ ...prev, [projectId]: false }));
    }
  };

  // Handle leaving project
  const handleLeaveProject = async (projectId: string, projectMemberId?: string) => {
    if (!projectMemberId) {
      console.error('Project member ID is required to leave project');
      return;
    }

    if (!window.confirm('Are you sure you want to leave this project?')) return;

    setLeavingProjects(prev => ({ ...prev, [projectId]: true }));
    try {
      await api.patch(`/projects/${projectId}/members/${projectMemberId}/invite`, {
        status: 'left'
      });

      // Refresh shared projects
      await fetchProjects();
    } catch (err: any) {
      console.error('Failed to leave project:', err);
      alert(err.response?.data?.detail || 'Failed to leave project');
    } finally {
      setLeavingProjects(prev => ({ ...prev, [projectId]: false }));
    }
  };

  // Get current projects based on active tab
  const getCurrentProjects = (): Project[] => {
    if (activeTab === 'myProjects') {
      return projects;
    } else if (activeTab === 'sharedWithMe') {
      // Return shared projects directly (they're already in Project format)
      return sharedProjects;
    } else if (activeTab === 'invitations') {
      // Convert invitations to Project format
      return invitations.map(inv => ({
        id: inv.project_id,
        platform_type: inv.platform_type,
        title: inv.project_title,
        description: inv.project_description || '',
        project_type: inv.project_type || 1,
        created_at: inv.invited_at ? new Date(inv.invited_at * 1000).toISOString() : new Date().toISOString(),
        updated_at: inv.invited_at ? new Date(inv.invited_at * 1000).toISOString() : undefined,
        user_id: inv.project_owner_email || '',
        client_name: '', // Invitations might not have client_name
      }));
    }
    return [];
  };

  // Filter projects based on search, last updated, and application type
  useEffect(() => {
    let filtered = [...getCurrentProjects()];

    // Filter by search query
    if (searchQuery.trim()) {
      filtered = filtered.filter(project =>
        project.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        project.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        project.client_name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Filter by application type
    filtered = filtered.filter(project => {
      const isWeb = project.project_type === 1 || !project.project_type;
      const isTyler = project.project_type === 2 && project.platform_type !== PlatformType.ServiceNow && project.platform_type !== PlatformType.PowerApps && project.platform_type !== PlatformType.Salesforce;
      const isServiceNow = project.project_type === 2 && project.platform_type === PlatformType.ServiceNow;
      const isPowerApps = project.project_type === 2 && project.platform_type === PlatformType.PowerApps;
      const isSalesforce = project.project_type === 2 && project.platform_type === PlatformType.Salesforce;
      return (isWeb && selectedAppTypes.web) || (isTyler && selectedAppTypes.tyler) || (isServiceNow && selectedAppTypes.serviceNow) || (isPowerApps && selectedAppTypes.powerApps) || (isSalesforce && selectedAppTypes.salesforce);
    });

    // Sort projects
    filtered.sort((a, b) => {
      if (sortBy === 'updated_at') {
        const dateA = new Date(a.updated_at || a.created_at).getTime();
        const dateB = new Date(b.updated_at || b.created_at).getTime();
        return dateB - dateA;
      } else if (sortBy === 'created_at') {
        const dateA = new Date(a.created_at).getTime();
        const dateB = new Date(b.created_at).getTime();
        return dateB - dateA;
      } else if (sortBy === 'alphabetical') {
        return a.title.localeCompare(b.title);
      }
      return 0;
    });

    setFilteredProjects(filtered);
  }, [projects, sharedProjects, invitations, activeTab, searchQuery, sortBy, selectedAppTypes]);

  const fetchProjects = async () => {
    setLoadingProjects(true);
    try {
      const res = await api.get('/projects/');
      // Map _id to id if needed and sort by creation date (latest first)
      const projects = res.data.map((p: any) => ({
        ...p,
        id: p.id || p._id,
      })).sort((a: Project, b: Project) => {
        // Sort by created_at in descending order (latest first)
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });
      
      // Separate owned and shared projects
      const ownedProjects = projects.filter((p: Project) => !p.is_shared);
      const sharedProjectsList = projects.filter((p: Project) => p.is_shared);
      
      setProjects(ownedProjects);
      setSharedProjects(sharedProjectsList);

      // Resume polling for any projects that are still cloning
      const stillCloning = ownedProjects.filter((p: Project) => p.clone_status === 'cloning');
      if (stillCloning.length > 0) {
        setCloningProjectIds(prev => {
          const next = new Set(prev);
          stillCloning.forEach((p: Project) => next.add(p.id));
          return next;
        });
      }
    } catch (err) {
      // handle error
    } finally {
      setLoadingProjects(false);
    }
  };

  const handleProjectClick = (projectId: string) => { 
    let project = projects.find(p => p.id === projectId);
    if(!project){
      project = sharedProjects.find(p => p.id === projectId);
    }
    const basePath = project?.project_type === 2 ? '/fedramp/chat' : '/chat';
    // Check localStorage for saved position
    const savedPosition = getProjectPosition(projectId);
    
    if (savedPosition) {
      if (savedPosition.currentMode === 'cab') {
        navigate(`${basePath}/${projectId}/cab`);
      } else if (savedPosition.currentMode === 'design' && savedPosition.designThreadId) {
        navigate(`${basePath}/${projectId}/design/${savedPosition.designThreadId}`);
      } else if (savedPosition.currentMode === 'build') {
        navigate(`${basePath}/${projectId}/build`);
      } else if (savedPosition.currentMode === 'tickets') {
        navigate(`${basePath}/${projectId}/tickets`);
      } else if (savedPosition.currentMode === 'design') {
        // Design mode but no thread_id, navigate to design mode and let it auto-select first thread
        navigate(`${basePath}/${projectId}/design`);
      } else {
        navigate(`${basePath}/${projectId}`);
      }
    } else {
      // Default behavior - no saved position, navigate to base path (will redirect to CAB)
      navigate(`${basePath}/${projectId}`);
    }
  };

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newProjectUserInput.title?.trim()) {
      setCreateProjectError('Project name is required');
      return;
    }
    if (newProjectUserInput.projectType === 2 && !newProjectUserInput.platformType) {
      setCreateProjectError('Platform type is required');
      return;
    }
    setCreatingProject(true);
    setCreateProjectError('');
    try {
      const payload: any = {
        title: newProjectUserInput.title,
        description: "", // Sending empty description as it's replaced by client_name
        client_name: newProjectUserInput.clientName,
        project_type: newProjectUserInput.projectType,
        custom_domain: newProjectUserInput.customDomain,
        created_at: new Date().toISOString(),
        user_id: user?.id,
      };

      // Add platform_type if project_type is 2
      if (newProjectUserInput.projectType === 2 && newProjectUserInput.platformType) {
        payload.platform_type = newProjectUserInput.platformType;
      }

      const response = await api.post('/projects/', payload);
      const newProject = response.data;

      // Reset form state
      setShowCreateProjectModal(false);
      setNewProjectUserInput({
        title: '',
        clientName: '',
        projectType: 1,
        platformType: undefined,
      });

      // Redirect to the newly created project (will default to CAB mode)
      const projectId = newProject.id || newProject._id;
      if (newProjectUserInput.projectType === 2) {
        navigate(`/fedramp/chat/${projectId}`);
      } else {
        navigate(`/chat/${projectId}`);
      }

      fetchProjects();
    } catch (err: any) {
      // Extract error message from the response
      let errorMessage = 'Failed to create project';

      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setCreateProjectError(errorMessage);
      console.error('Failed to create project:', err);
    } finally {
      setCreatingProject(false);
    }
  };

  const openEditModal = (project: Project) => {
    setEditProjectId(project.id);
    setEditProjectName(project.title);
    // setEditProjectDesc(project.description);
    setEditProjectClientName(project.client_name || "");
    setEditProjectCustomDomain(project.custom_domain || "");
    setEditError('');
    setEditModalOpen(true);
  };

  const openStatsModal = (project: Project) => {
    setStatsProjectId(project.id);
    setStatsProjectTitle(project.title);
    setShowStatsModal(true);
  };

  const handleEditProject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editProjectName.trim()) {
      setEditError('Project name is required');
      return;
    }
    setEditLoading(true);
    setEditError('');
    try {
      await api.put(`/projects/${editProjectId}`, {
        title: editProjectName,
        description: "", // Sending empty description
        client_name: editProjectClientName,
        custom_domain: editProjectCustomDomain,
      });
      setEditModalOpen(false);
      setEditProjectId(null);
      setEditProjectName('');
      // setEditProjectDesc('');
      setEditProjectClientName('');
      setEditProjectCustomDomain('');
      fetchProjects();
    } catch (err) {
      setEditError('Failed to update project');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteProject = async (projectId: string) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    setDeleteLoading(true);
    setDeleteError('');
    try {
      await api.delete(`/projects/${projectId}`);
      fetchProjects();
    } catch (err) {
      setDeleteError('Failed to delete project');
    } finally {
      setDeleteLoading(false);
    }
  };

  const onSelectCloneProject = (project: Project) => {
    setSelectedCloneProject(project);
    setCloneUserInput({
      title: `${project.title} (Clone)`,
      clientName: project.client_name,
      projectType: project.project_type,
      platformType: project.platform_type as PlatformType | undefined,
    });
  };

  const handleCloneProject = async (project: Project) => {
    setIsCloning(true);
    try {
      const response = await api.post(`/projects/${project.id}/clone-project`, {
        title: cloneUserInput?.title,
        description: project.description,
        client_name: cloneUserInput?.clientName,
        project_type: cloneUserInput?.projectType,
        platform_type: cloneUserInput?.platformType,
      });

      // Add the new cloning project to the list
      const projectId = response.data.id || response.data._id;

      if (!projectId) {
        console.error('Clone response missing project ID:', response.data);
        alert('Clone started but failed to get project ID');
        return;
      }

      // Normalise id so polling p.id === projectId always matches
      const newProject = { ...response.data, id: projectId };

      setProjects(prev => [newProject, ...prev]);
      setCloningProjectIds(prev => new Set([...prev, projectId]));

      setSelectedCloneProject(undefined);
      setCloneUserInput(undefined);
    } catch (err) {
      alert('Failed to start clone');
    } finally {
      setIsCloning(false);
    }
  };

  const handleRetryClone = async (projectId: string) => {
    try {
      const response = await api.post(`/projects/${projectId}/clone-retry`);
      const updatedProject = { ...response.data, id: projectId };
      setProjects(prev => prev.map(p => p.id === projectId ? updatedProject : p));
      setCloningProjectIds(prev => new Set([...prev, projectId]));
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || 'Failed to retry clone';
      alert(errorMsg);
    }
  };

  const handleDeleteClone = (projectId: string, cloneStatus?: string) => {
    setDeleteCloneError('');
    setDeleteCloneConfirm({ projectId, cloneStatus });
  };

  const confirmDeleteClone = async () => {
    if (!deleteCloneConfirm) return;
    const { projectId } = deleteCloneConfirm;
    setDeleteCloneLoading(true);
    setDeleteCloneError('');
    try {
      await api.delete(`/projects/${projectId}`);
      setProjects(prev => prev.filter(p => p.id !== projectId));
      setCloningProjectIds(prev => {
        const next = new Set(prev);
        next.delete(projectId);
        return next;
      });
      setDeleteCloneConfirm(null);
    } catch (err) {
      setDeleteCloneError('Failed to delete clone. Please try again.');
    } finally {
      setDeleteCloneLoading(false);
    }
  };

  const handleManageCollaborations = (project: Project) => {
    setSelectedProject(project);
    setIsManageCollaborationsModalOpen(true);
  };

  const handleProjectUpdate = (updatedProject: Project) => {
    // Update the selected project state
    setSelectedProject(updatedProject);

    // Also update the project in the projects list
    setProjects(prev => prev.map(p =>
      p.id === updatedProject.id ? updatedProject : p
    ));
  };

  // Keep a ref so the interval callback always sees the latest cloningProjectIds
  // without restarting the interval on every Set change
  const cloningProjectIdsRef = useRef<Set<string>>(cloningProjectIds);
  useEffect(() => {
    cloningProjectIdsRef.current = cloningProjectIds;
  }, [cloningProjectIds]);

  // Single persistent polling interval — runs as long as any clone is in progress
  useEffect(() => {
    const pollInterval = setInterval(async () => {
      const projectIds = Array.from(cloningProjectIdsRef.current);
      if (projectIds.length === 0) return;

      for (const projectId of projectIds) {
        try {
          const response = await api.get(`/projects/${projectId}`);
          const updatedProject = { ...response.data, id: response.data.id || response.data._id };

          setProjects(prev => prev.map(p => p.id === projectId ? updatedProject : p));

          // Stop polling this project once clone is no longer in progress
          if (updatedProject.clone_status !== 'cloning') {
            setCloningProjectIds(prev => {
              const next = new Set(prev);
              next.delete(projectId);
              return next;
            });
          }
        } catch (err) {
          // Stop polling on persistent error
          setCloningProjectIds(prev => {
            const next = new Set(prev);
            next.delete(projectId);
            return next;
          });
        }
      }
    }, 4000);

    return () => clearInterval(pollInterval);
  }, []); // Single interval — never restarts

  return (
    <div className="bg-bg-secondary-alt h-screen flex flex-col overflow-hidden">
      <Navbar
        user={user}
        onHomeClick={() => navigate('/')}
        actions={[
          {
            label: <Icon name={theme === 'dark' ? IconName.Sun : IconName.Moon} size='sm' variant='secondary' />,
            onClick: toggleTheme,
            tooltip: theme === 'dark' ? 'Light Mode' : 'Dark Mode',
          },
          {
            label: <Icon name={IconName.Bell} size='sm' variant='secondary' />,
            onClick: () => {
              // Notification handler - placeholder for future implementation
            },
            tooltip: 'Notifications',
          },
          {
            label: <Icon name={IconName.HelpCircle} size='sm' variant='secondary' />,
            onClick: () => {
              window.open('https://docs.bluegenai.com/', '_blank', 'noopener,noreferrer');
            },
            tooltip: 'Help',
          }
        ]}
      />

      <div className="flex-1 min-h-0 overflow-hidden">
        <div className="max-w-bg-v2-3xl w-full mx-auto h-full flex flex-col min-h-0 px-8">
          <div className="pt-6 pb-0">
            <div className="flex items-center justify-between mb-4">
              <div>
                <Text variant="display-xs" weight="bold" color="primary" className="mb-2">Projects</Text>
                <Text variant="text-md" weight="medium" color="tertiary">Manage and organize your development projects</Text>
              </div>
              <Button
                variant="primary"
                label={<div className="flex items-center gap-2"><Icon name={IconName.Plus} size="sm" inheritColor /> Create Project</div>}
                onClick={() => setShowCreateProjectModal(true)}
                className="shadow-sm hover:shadow-md transition-all"
              />
            </div>

            <SearchAndFilters
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              sortBy={sortBy}
              onSortChange={setSortBy}
              selectedAppTypes={selectedAppTypes}
              onAppTypesChange={setSelectedAppTypes}
            />
          </div>

          <div className="border-t border-border-secondary mt-8 mb-8 pt-0 pb-0"></div>

          {/* Tab Button Group */}
          <div className="mb-6">
            <ProjectTabs
              activeTab={activeTab}
              onTabChange={setActiveTab}
              myProjectsCount={projects.length}
              sharedProjectsCount={sharedProjects.length}
              invitationsCount={invitations.length}
            />
          </div>

          {(loadingProjects || (activeTab === 'sharedWithMe' && loadingProjects) || (activeTab === 'invitations' && loadingInvitations)) ? (
            <div className="overflow-y-auto overflow-x-hidden pr-0 pb-10 scrollbar-hide" style={{ maxHeight: 'calc(100vh - 300px)' }}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Render 6 skeleton cards with staggered animation */}
                {[...Array(6)].map((_, index) => (
                  <ProjectCardSkeleton key={index} index={index} />
                ))}
              </div>
            </div>
          ) : (
            <div className="overflow-y-auto overflow-x-hidden pr-0 pb-10 scrollbar-hide" style={{ maxHeight: 'calc(100vh - 300px)' }}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredProjects.map((project, index) => {
                  let dateDisplay = "";
                  // console.log('sortBy:', sortBy);
                  if (sortBy === 'updated_at' || sortBy === 'alphabetical') {
                    const timestampToUse = (project.updated_at as string | number | undefined) || project.created_at;
                    dateDisplay = `Updated ${getRelativeTime(timestampToUse)}`;
                  } else if (sortBy === 'created_at') {
                    dateDisplay = `Created ${getRelativeTime(project.created_at)}`;
                  }
                  // console.log('dateDisplay:', dateDisplay);

                  return (
                    <div
                      key={project.id}
                      className="animate-fade-in-up-smooth opacity-0"
                      style={{
                        animationDelay: `${index * 0.05}s`,
                        animationFillMode: 'forwards'
                      }}
                    >
                      <ProjectCard
                        projectType={project.project_type || 1}
                        projectName={project.title}
                        clientName={project.client_name}
                        dateDisplay={dateDisplay}
                        applicationType={project.project_type === 2
                          ? (project.platform_type === PlatformType.ServiceNow 
                              ? ApplicationType.ServiceNow 
                              : project.platform_type === PlatformType.PowerApps
                              ? ApplicationType.PowerApps
                              : project.platform_type === PlatformType.Salesforce
                              ? ApplicationType.Salesforce
                              : ApplicationType.Tyler)
                          : ApplicationType.Web}
                        projectId={project.id}
                        onClick={() => handleProjectClick(project.id)}
                        onEdit={() => openEditModal(project)}
                        onDelete={() => handleDeleteProject(project.id)}
                        onClone={() => onSelectCloneProject(project)}
                        onStats={() => openStatsModal(project)}
                        cloneStatus={project.clone_status}

                        onRetryClone={() => handleRetryClone(project.id)}
                        onDeleteClone={() => handleDeleteClone(project.id, project.clone_status)}
                        isCloning={isCloning}
                        deleteLoading={deleteLoading}
                        onManageCollaborations={() => handleManageCollaborations(project)}
                        sharedBy={getSharedProjectInfo(project.id).sharedBy}
                        role={getSharedProjectInfo(project.id).role}
                        isInvitation={activeTab === 'invitations'}
                        onAccept={activeTab === 'invitations' ? () => {
                          const info = getSharedProjectInfo(project.id);
                          if (info.projectMemberId) {
                            handleAcceptInvitation(project.id, info.projectMemberId);
                          }
                        } : undefined}
                        onDecline={activeTab === 'invitations' ? () => {
                          const info = getSharedProjectInfo(project.id);
                          if (info.projectMemberId) {
                            handleDeclineInvitation(project.id, info.projectMemberId);
                          }
                        } : undefined}
                        isAccepting={acceptingInvitations[project.id] || false}
                        isDeclining={decliningInvitations[project.id] || false}
                        onLeave={activeTab === 'sharedWithMe' ? () => {
                          const info = getSharedProjectInfo(project.id);
                          if (info.projectMemberId) {
                            handleLeaveProject(project.id, info.projectMemberId);
                          }
                        } : undefined}
                        isLeaving={leavingProjects[project.id] || false}
                      />
                    </div>
                  );
                })}
              </div>
              {filteredProjects.length === 0 && !loadingProjects && !(activeTab === 'sharedWithMe' && loadingProjects) && !(activeTab === 'invitations' && loadingInvitations) && (
                <div className="flex flex-col items-center justify-center h-64 text-center">
                  <div className="bg-bg-secondary p-4 rounded-full mb-4">
                    <Icon name={IconName.Search} size="xl" className="text-text-tertiary" />
                  </div>
                  <Text variant="text-lg" weight="medium" color="primary">No projects found</Text>
                  {(searchQuery.trim() || !selectedAppTypes.web || !selectedAppTypes.tyler || !selectedAppTypes.serviceNow || !selectedAppTypes.powerApps || !selectedAppTypes.salesforce) && (
                    <Text variant="text-sm" color="secondary">Try adjusting your search or filters</Text>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <CreateProjectModal
        isOpen={showCreateProjectModal}
        onClose={() => {
          setShowCreateProjectModal(false);
          // Reset form state when closing
          setNewProjectUserInput({
            title: '',
            clientName: '',
            projectType: 1,
            platformType: undefined,
          });
          setCreateProjectError('');
        }}
        onSubmit={handleCreateProject}
        loading={creatingProject}
        error={createProjectError}
        userInput={newProjectUserInput}
        setUserInput={setNewProjectUserInput}
      />

      <EditProjectModal
        isOpen={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          // Reset form state when closing
          setEditProjectId(null);
          setEditProjectName('');
          setEditProjectClientName('');
          setEditError('');
        }}
        onSubmit={handleEditProject}
        loading={editLoading}
        error={editError}
        name={editProjectName}
        setName={setEditProjectName}
        clientName={editProjectClientName}
        setClientName={setEditProjectClientName}
        customDomain={editProjectCustomDomain}
        setCustomDomain={setEditProjectCustomDomain}
        projectType={projects.find(p => p.id === editProjectId)?.project_type}
        promptVersion={projects.find(p => p.id === editProjectId)?.prompt_version}
        projectId={editProjectId || undefined}
      />

      <CloneProjectModal
        isOpen={selectedCloneProject !== undefined}
        onClose={() => {
          setSelectedCloneProject(undefined);
          // Reset form state when closing
          setCloneUserInput(undefined);
        }}
        onSubmit={(e: React.FormEvent) => { e.preventDefault(); if (selectedCloneProject) handleCloneProject(selectedCloneProject); }}
        loading={isCloning}
        userInput={cloneUserInput || {}}
        setUserInput={setCloneUserInput}
        sourceProjectType={selectedCloneProject?.project_type}
        sourcePlatformType={selectedCloneProject?.platform_type}
      />

      <CreditViewPanel
        isOpen={showCreditViewPanel}
        onClose={() => setShowCreditViewPanel(false)}
      />
      <ProjectStatsModal
        isOpen={showStatsModal}
        onClose={() => setShowStatsModal(false)}
        projectId={statsProjectId}
        projectTitle={statsProjectTitle}
      />
      <ManageCollaborationModal
        isOpen={isManageCollaborationsModalOpen}
        onClose={() => setIsManageCollaborationsModalOpen(false)}
        selectedProject={selectedProject}
        onProjectUpdate={handleProjectUpdate}
      />

      <Modal
        isOpen={!!deleteCloneConfirm}
        onClose={() => { if (!deleteCloneLoading) setDeleteCloneConfirm(null); }}
        title={deleteCloneConfirm?.cloneStatus === 'cloning' ? 'Cancel Clone' : 'Delete Clone'}
        size="sm"
        onSubmit={confirmDeleteClone}
        onCancel={() => { if (!deleteCloneLoading) setDeleteCloneConfirm(null); }}
        submitLabel={deleteCloneConfirm?.cloneStatus === 'cloning' ? 'Cancel Clone' : 'Delete Clone'}
        cancelLabel="Keep"
        submitLoading={deleteCloneLoading}
      >
        <Text color="secondary">
          {deleteCloneConfirm?.cloneStatus === 'cloning'
            ? 'Cancel this in-progress clone? The clone job will be stopped and the project will be removed.'
            : 'Delete this failed clone? This action cannot be undone.'}
        </Text>
        {deleteCloneError && (
          <Text color="error" className="mt-2">{deleteCloneError}</Text>
        )}
      </Modal>
    </div>
  );
};

export default Dashboard;
